import React, { useEffect, useState, useMemo } from 'react';
import Swal from 'sweetalert2';
import {
    collection,
    doc,
    getDoc,
    getDocs,
    onSnapshot,
    orderBy,
    query,
    Timestamp,
    where,
    writeBatch
} from 'firebase/firestore';
import { auth, db } from '../../firebase';
import "../../App.css";
import moment from 'moment';
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    TableHead,
    Paper,
    IconButton,
    Stack,
    Pagination,
    Chip,
    InputAdornment,
    Tabs,
    Tab,
    Fade
} from "@mui/material";

import {
    Visibility,
    Close,
    Search as SearchIcon,
    Hotel,
    EventAvailable,
    ExitToApp,
    Circle
} from "@mui/icons-material";

// Premium Dark Theme Palette
const BG_DARK = "#06362D";
const PANEL_DARK = "#06362D";
const DEEP_DARK = "#04241E"; // Used for deep backgrounds (table header, search field)
const BORDER_DARK = "#D1A505";
const TEXT_PRIMARY = "#DEE2B1";
const TEXT_SECONDARY = "#D1A505";
const NEON_GREEN = "#FFD500"; 
const STRICT_DARK_BORDER = "rgba(0, 0, 0, 0.5)"; // Replaces all white/light borders

// --- CONTINUOUS ANIMATION CSS ---
const animationStyles = `
  /* 1. Continuous Border Glowing / Pulsing (Dark to Gold) */
  @keyframes continuousBorderPulse {
    0% { border-color: ${STRICT_DARK_BORDER}; box-shadow: 0 8px 30px rgba(0,0,0,0.5); }
    50% { border-color: rgba(209, 165, 5, 0.4); box-shadow: 0 0 25px rgba(255, 213, 0, 0.1), inset 0 0 10px rgba(0,0,0,0.5); }
    100% { border-color: ${STRICT_DARK_BORDER}; box-shadow: 0 8px 30px rgba(0,0,0,0.5); }
  }

  /* 2. Fast Blinking for Live Data Dot */
  @keyframes fastBlink {
    0%, 100% { opacity: 1; transform: scale(1); box-shadow: 0 0 8px ${NEON_GREEN}; }
    50% { opacity: 0.3; transform: scale(0.85); box-shadow: 0 0 0px transparent; }
  }

  /* 3. Continuous Breathing (Zoom In/Out) for Headers */
  @keyframes continuousBreathe {
    0%, 100% { transform: scale(1); text-shadow: 0 4px 10px rgba(0,0,0,0.6); }
    50% { transform: scale(1.01); text-shadow: 0 6px 15px rgba(255, 213, 0, 0.2); }
  }

  /* 4. Subtle Table Row Pulse */
  @keyframes tableRowPulse {
    0%, 100% { background-color: transparent; }
    50% { background-color: rgba(0, 0, 0, 0.15); }
  }

  /* 5. Icon Pulsing */
  @keyframes iconPulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.1); color: ${TEXT_PRIMARY}; }
  }
  
  /* Initial load fade */
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(15px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .anim-border-glow {
    animation: continuousBorderPulse 5s ease-in-out infinite;
  }
  .anim-live-dot {
    animation: fastBlink 1.5s infinite;
    border-radius: 50%;
  }
  .anim-breathe-text {
    animation: continuousBreathe 4s ease-in-out infinite;
  }
  .anim-icon-pulse {
    animation: iconPulse 3s ease-in-out infinite;
  }
  .anim-table-row {
    animation: tableRowPulse 4s infinite;
  }
  .page-fade-in {
    animation: fadeIn 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  }
  
  /* Custom Scrollbar for Table */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  ::-webkit-scrollbar-track {
    background: rgba(0,0,0,0.3); 
  }
  ::-webkit-scrollbar-thumb {
    background: ${BORDER_DARK}; 
    border-radius: 4px;
  }
  ::-webkit-scrollbar-thumb:hover {
    background: ${NEON_GREEN}; 
  }
`;

function Viewreservations() {
    const Toast = Swal.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        background: DEEP_DARK,
        color: TEXT_PRIMARY,
        didOpen: (toast) => {
            toast.addEventListener('mouseenter', Swal.stopTimer);
            toast.addEventListener('mouseleave', Swal.resumeTimer);
        }
    });

    const [bookings, setbookings] = useState([]);
    const [propertyId, setpropertyId] = useState(null);
    const [username, setusername] = useState("");
    const [userData, setUserData] = useState(null);
    const [searchText, setsearchText] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage] = useState(10);
    const [currentTab, setCurrentTab] = useState(0); // 0: Live, 1: Upcoming, 2: Checkout

    // 1️⃣ Authenticate and fetch logged-in user profile details
    useEffect(() => {
        return auth.onAuthStateChanged(async (user) => {
            if (!user) return;
            try {
                const snap = await getDoc(doc(db, "authroles", user.uid));
                if (snap.exists()) {
                    const data = snap.data();
                    setpropertyId(data.propertyId);
                    setusername(data.username || data.name || "Staff");
                    setUserData({ uid: user.uid, ...data });
                }
            } catch (error) {
                console.error("Error fetching user role context:", error);
            }
        });
    }, []);

    // 2️⃣ Live Realtime Firestore Fetch ordered by check-in date
    useEffect(() => {
        if (!propertyId) return;

        const q = query(
            collection(db, 'reservations'),
            where('propertyId', '==', propertyId),
            where('status', '==', 'Booked'), 
            orderBy('checkindate', 'asc')
        );

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const fetchedBookings = querySnapshot.docs.map(doc => ({
                id: doc.id,
                data: doc.data()
            }));
            setbookings(fetchedBookings);
        }, (error) => {
            console.error("Firestore subscription error:", error);

            if (error.code === 'failed-precondition') {
                console.warn("Index required. Fetching without database sorting as fallback...");
                const fallbackQuery = query(
                    collection(db, 'reservations'),
                    where('propertyId', '==', propertyId),
                    where('status', '==', 'Booked')
                );
                return onSnapshot(fallbackQuery, (fallbackSnapshot) => {
                    const fetchedBookings = fallbackSnapshot.docs.map(doc => ({
                        id: doc.id,
                        data: doc.data()
                    })).sort((a, b) => new Date(a.data.checkindate) - new Date(b.data.checkindate));
                    setbookings(fetchedBookings);
                });
            }

            Toast.fire({
                icon: "error",
                title: "Failed to sync reservations data."
            });
        });

        return unsubscribe;
    }, [propertyId]);

    // 3️⃣ Intelligent Categorization & Filtering Logic
    const categorizedData = useMemo(() => {
        const today = moment().startOf('day');

        const searched = bookings.filter((item) => {
            const customerName = item.data?.customers?.custname || "";
            const mobileNo = item.data?.customers?.mobile || "";
            const bookingSource = item.data?.bookingsource || "";
            const targetRooms = (item.data?.rooms || []).map(r => r.roomno || "").join(" ");
            const combinedSearchString = `${customerName} ${mobileNo} ${bookingSource} ${targetRooms}`.toLowerCase();
            return combinedSearchString.includes(searchText.toLowerCase());
        });

        const live = [];
        const upcoming = [];
        const checkout = [];

        searched.forEach(b => {
            const checkIn = moment(b.data.checkindate, 'YYYY-MM-DD').startOf('day');
            const checkOut = moment(b.data.checkoutdate, 'YYYY-MM-DD').startOf('day');

            if (checkOut.isSameOrBefore(today)) {
                checkout.push(b);
            } else if (checkIn.isAfter(today)) {
                upcoming.push(b);
            } else {
                live.push(b);
            }
        });

        const sortDesc = (arr) => arr.sort((a, b) => {
            const dateA = new Date(a.createdAt || a.data?.createdAt || 0);
            const dateB = new Date(b.createdAt || b.data?.createdAt || 0);
            return dateB - dateA; 
        });

        return {
            live: sortDesc(live),
            upcoming: sortDesc(upcoming),
            checkout: sortDesc(checkout)
        };
    }, [bookings, searchText]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchText, currentTab]);

    const activeDataArray = currentTab === 0 ? categorizedData.live : currentTab === 1 ? categorizedData.upcoming : categorizedData.checkout;

    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const currentRows = activeDataArray.slice(indexOfFirstRow, indexOfLastRow);
    const totalPages = Math.ceil(activeDataArray.length / rowsPerPage);

    // 4️⃣ Secure Atomic Booking & Room Inventory Cancellation Routine
    const cancelBooking = async (bookingId, roomUsed = "No") => {
        if (!userData) {
            Toast.fire({ icon: "error", title: "Action denied. User context missing." });
            return;
        }

        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "This will cancel the booking and release all associated rooms!",
            icon: 'warning',
            background: DEEP_DARK,
            color: TEXT_PRIMARY,
            showCancelButton: true,
            confirmButtonColor: '#991b1b', // Darker red for theme consistency
            cancelButtonColor: '#475569',
            confirmButtonText: 'Yes, cancel it!'
        });

        if (!result.isConfirmed) return;

        try {
            const bookingRef = doc(db, "reservations", bookingId);
            const bookingSnap = await getDoc(bookingRef);

            if (!bookingSnap.exists()) {
                Toast.fire({ icon: "error", title: "Booking record not found." });
                return;
            }

            const bookingData = bookingSnap.data();
            const checkinDetailsQuery = query(collection(db, 'checkindetails'), where('bookingid', '==', bookingId));
            const payHistoryQuery = query(collection(db, 'payhistory'), where('bookingid', '==', bookingId));

            const [checkinDetailsSnapshot, payHistorySnapshot] = await Promise.all([
                getDocs(checkinDetailsQuery),
                getDocs(payHistoryQuery)
            ]);

            const batch = writeBatch(db);

            batch.update(bookingRef, { status: "Canceled" });

            checkinDetailsSnapshot.docs.forEach(item => {
                batch.update(doc(db, "checkindetails", item.id), { status: "Canceled" });
            });
            payHistorySnapshot.docs.forEach(item => {
                batch.update(doc(db, "payhistory", item.id), { status: "Canceled" });
            });
            const rooms = bookingData.rooms || [];
            const logRef = doc(collection(db, 'logs'));
            batch.set(logRef, {
                bookingid: bookingId,
                customerId: bookingData.customerId || "",
                customers: bookingData.customers || {},
                rooms: rooms,
                uid: userData.uid,
                username: username,
                createdAt: Timestamp.now(),
                type: 'Booking Cancellation',
            });

            await batch.commit();
            Toast.fire({ icon: "success", title: 'Reservation Successfully Canceled' });

        } catch (error) {
            console.error("Cancellation system failure detail:", error);
            Toast.fire({
                icon: "error",
                title: 'Failed to process cancellation',
                text: error.message
            });
        }
    };

    return (
        <Box sx={{ minHeight: "100vh", backgroundColor: BG_DARK, overflowX: "hidden" }}>
            <style>{animationStyles}</style>

            <Box className="page-fade-in" p={2} >
                
                {/* Header Section */}
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                    <Typography variant="h6" fontWeight={800} color={TEXT_PRIMARY} className="anim-breathe-text" sx={{ letterSpacing: '0.5px' }}>
                        Reservation Control Matrix
                    </Typography>
                    
                    <Box sx={{ background: 'rgba(0, 0, 0, 0.4)', color: NEON_GREEN, padding: '8px 16px', borderRadius: 8, fontSize: '0.85rem', fontWeight: 700, display: 'flex', alignItems: 'center', border: `1px solid ${STRICT_DARK_BORDER}` }}>
                        <Circle sx={{ width: 10, height: 10, mr: 1, color: NEON_GREEN }} className="anim-live-dot" /> Live System
                    </Box>
                </Box>

                <Card className="anim-border-glow" sx={{ borderRadius: "16px", backgroundColor: PANEL_DARK, border: `1px solid ${STRICT_DARK_BORDER}`, transition: 'all 0.3s' }}>
                    <CardContent sx={{ p: { xs: 2, md: 4 } }}>
                        
                        {/* Interactive Tabs Header */}
                        <Box sx={{ borderBottom: 1, borderColor: STRICT_DARK_BORDER, mb: 3 }}>
                            <Tabs 
                                value={currentTab} 
                                onChange={(e, newValue) => setCurrentTab(newValue)} 
                                variant="fullWidth"
                                sx={{
                                    "& .MuiTabs-indicator": { backgroundColor: NEON_GREEN, height: 3, borderRadius: '4px 4px 0 0' },
                                    "& .MuiTab-root": { color: TEXT_SECONDARY, fontWeight: 600, fontSize: '0.95rem', textTransform: 'none', transition: 'all 0.3s' },
                                    "& .Mui-selected": { color: `${NEON_GREEN} !important`, textShadow: '0 0 15px rgba(255, 213, 0, 0.3)' }
                                }}
                            >
                                <Tab 
                                    icon={<Hotel fontSize="small"/>} 
                                    iconPosition="start" 
                                    label={`Currently Live (${categorizedData.live.length})`} 
                                />
                                <Tab 
                                    icon={<EventAvailable fontSize="small"/>} 
                                    iconPosition="start" 
                                    label={`Upcoming Bookings (${categorizedData.upcoming.length})`} 
                                />
                                <Tab 
                                    icon={<ExitToApp fontSize="small"/>} 
                                    iconPosition="start" 
                                    label={`Checkout Alert (${categorizedData.checkout.length})`} 
                                    sx={{ 
                                        color: categorizedData.checkout.length > 0 ? '#ef4444' : 'inherit',
                                        "&.Mui-selected": { color: categorizedData.checkout.length > 0 ? '#ef4444 !important' : `${NEON_GREEN} !important` }
                                    }}
                                />
                            </Tabs>
                        </Box>

                        {/* Search Bar */}
                        <Box mb={4} maxWidth={400}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Search by name, phone, source or room..."
                                value={searchText}
                                onChange={(e) => setsearchText(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: TEXT_SECONDARY, fontSize: 20 }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "10px",
                                        backgroundColor: DEEP_DARK,
                                        color: TEXT_PRIMARY,
                                        fontSize: "0.9rem",
                                        border: `1px solid ${STRICT_DARK_BORDER}`,
                                        boxShadow: "inset 0 2px 4px rgba(0,0,0,0.3)",
                                        transition: 'all 0.3s',
                                        "&:hover": { borderColor: "rgba(0,0,0,0.8)", backgroundColor: "rgba(0,0,0,0.4)" },
                                        "&.Mui-focused": { borderColor: BORDER_DARK, boxShadow: `0 0 10px rgba(209, 165, 5, 0.15)` }
                                    },
                                    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                                    "& input::placeholder": { color: TEXT_SECONDARY, opacity: 0.6 }
                                }}
                            />
                        </Box>

                        {/* Table Layout Structure Container */}
                        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: "12px", border: `1px solid ${STRICT_DARK_BORDER}`, backgroundColor: DEEP_DARK, overflow: 'hidden', boxShadow: "inset 0 4px 10px rgba(0,0,0,0.2)" }}>
                            <Table size="medium">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                                        <TableCell sx={{ color: TEXT_SECONDARY, fontWeight: 700, fontSize: "0.8rem", borderBottom: `1px solid ${STRICT_DARK_BORDER}` }}>Sr</TableCell>
                                        <TableCell sx={{ color: TEXT_SECONDARY, fontWeight: 700, fontSize: "0.8rem", borderBottom: `1px solid ${STRICT_DARK_BORDER}` }}>Action</TableCell>
                                        <TableCell sx={{ color: TEXT_SECONDARY, fontWeight: 700, fontSize: "0.8rem", borderBottom: `1px solid ${STRICT_DARK_BORDER}` }}>Customer</TableCell>
                                        <TableCell sx={{ color: TEXT_SECONDARY, fontWeight: 700, fontSize: "0.8rem", borderBottom: `1px solid ${STRICT_DARK_BORDER}` }}>Source</TableCell>
                                        <TableCell sx={{ color: TEXT_SECONDARY, fontWeight: 700, fontSize: "0.8rem", borderBottom: `1px solid ${STRICT_DARK_BORDER}` }}>Check-in</TableCell>
                                        <TableCell sx={{ color: TEXT_SECONDARY, fontWeight: 700, fontSize: "0.8rem", borderBottom: `1px solid ${STRICT_DARK_BORDER}` }}>Check-out</TableCell>
                                        <TableCell sx={{ color: TEXT_SECONDARY, fontWeight: 700, fontSize: "0.8rem", borderBottom: `1px solid ${STRICT_DARK_BORDER}` }}>Rooms</TableCell>
                                        <TableCell sx={{ color: TEXT_SECONDARY, fontWeight: 700, fontSize: "0.8rem", borderBottom: `1px solid ${STRICT_DARK_BORDER}` }}>Plans</TableCell>
                                        <TableCell sx={{ color: TEXT_SECONDARY, fontWeight: 700, fontSize: "0.8rem", borderBottom: `1px solid ${STRICT_DARK_BORDER}` }}>Adults</TableCell>
                                        <TableCell align="right" sx={{ color: TEXT_SECONDARY, fontWeight: 700, fontSize: "0.8rem", borderBottom: `1px solid ${STRICT_DARK_BORDER}` }}>Total</TableCell>
                                        <TableCell align="right" sx={{ color: TEXT_SECONDARY, fontWeight: 700, fontSize: "0.8rem", borderBottom: `1px solid ${STRICT_DARK_BORDER}` }}>Paid</TableCell>
                                        <TableCell align="right" sx={{ color: TEXT_SECONDARY, fontWeight: 700, fontSize: "0.8rem", borderBottom: `1px solid ${STRICT_DARK_BORDER}` }}>Remaining</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody sx={{ backgroundColor: PANEL_DARK }}>
                                    {currentRows.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={12} align="center" sx={{ py: 8, borderBottom: 'none' }}>
                                                <Box className="anim-icon-pulse" sx={{ opacity: 0.5, mb: 2 }}>
                                                    <Hotel sx={{ fontSize: 40, color: TEXT_SECONDARY }} />
                                                </Box>
                                                <Typography sx={{ color: TEXT_SECONDARY, fontSize: "0.95rem", fontWeight: 500 }}>
                                                    No reservations found in this category.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}

                                    {currentRows.map((item, index) => (
                                        <Fade in={true} timeout={300 + (index * 100)} key={item.id}>
                                            <TableRow 
                                                hover 
                                                className="anim-table-row"
                                                sx={{ 
                                                    "&:last-child td, &:last-child th": { border: 0 },
                                                    transition: 'all 0.2s',
                                                    "&:hover": { backgroundColor: "rgba(255, 213, 0, 0.05) !important", transform: 'scale(1.001)' } 
                                                }}
                                            >
                                                <TableCell sx={{ fontSize: "0.85rem", color: TEXT_SECONDARY, borderBottom: `1px solid ${STRICT_DARK_BORDER}` }}>
                                                    {indexOfFirstRow + index + 1}
                                                </TableCell>

                                                <TableCell sx={{ borderBottom: `1px solid ${STRICT_DARK_BORDER}` }}>
                                                    <Stack direction="row" spacing={1}>
                                                        <IconButton
                                                            size="small"
                                                            href={`/reservation-details/${item.id}`}
                                                            sx={{ 
                                                                p: 0.8, backgroundColor: "rgba(0, 0, 0, 0.3)", color: NEON_GREEN, borderRadius: '8px',
                                                                border: `1px solid ${STRICT_DARK_BORDER}`,
                                                                transition: 'all 0.2s',
                                                                "&:hover": { backgroundColor: "rgba(209, 165, 5, 0.2)", transform: 'translateY(-2px)' } 
                                                            }}
                                                        >
                                                            <Visibility sx={{ fontSize: 16 }} />
                                                        </IconButton>

                                                        <IconButton
                                                            size="small"
                                                            onClick={() => cancelBooking(item.id, "No")}
                                                            sx={{ 
                                                                p: 0.8, backgroundColor: "rgba(0, 0, 0, 0.3)", color: "#ef4444", borderRadius: '8px',
                                                                border: `1px solid ${STRICT_DARK_BORDER}`,
                                                                transition: 'all 0.2s',
                                                                "&:hover": { backgroundColor: "rgba(239, 68, 68, 0.2)", transform: 'translateY(-2px)' } 
                                                            }}
                                                        >
                                                            <Close sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    </Stack>
                                                </TableCell>

                                                <TableCell sx={{ borderBottom: `1px solid ${STRICT_DARK_BORDER}` }}>
                                                    <Typography fontWeight={700} sx={{ color: TEXT_PRIMARY, fontSize: "0.85rem", letterSpacing: '0.3px' }}>
                                                        {item.data.customers?.custname || "—"}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: TEXT_SECONDARY, display: "block", fontSize: "0.75rem", mt: 0.5 }}>
                                                        {item.data.customers?.mobile || "—"}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell sx={{ fontSize: "0.85rem", color: TEXT_PRIMARY, borderBottom: `1px solid ${STRICT_DARK_BORDER}` }}>
                                                    {item.data.bookingsource || "—"}
                                                </TableCell>

                                                <TableCell sx={{ fontSize: "0.85rem", color: TEXT_PRIMARY, borderBottom: `1px solid ${STRICT_DARK_BORDER}` }}>
                                                    {item.data.checkindate ? moment(item.data.checkindate).format("DD MMM YYYY") : "—"}
                                                </TableCell>

                                                <TableCell sx={{ fontSize: "0.85rem", borderBottom: `1px solid ${STRICT_DARK_BORDER}`, fontWeight: currentTab === 2 ? 700 : 400, color: currentTab === 2 ? "#ef4444" : TEXT_PRIMARY }}>
                                                    {item.data.checkoutdate ? moment(item.data.checkoutdate).format("DD MMM YYYY") : "—"}
                                                </TableCell>

                                                <TableCell sx={{ borderBottom: `1px solid ${STRICT_DARK_BORDER}` }}>
                                                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                                                        {(item.data.rooms || []).map((r, i) => (
                                                            <Chip
                                                                key={i}
                                                                label={r.roomno}
                                                                size="small"
                                                                sx={{ height: "22px", fontSize: "0.7rem", backgroundColor: "rgba(0, 0, 0, 0.3)", color: TEXT_PRIMARY, fontWeight: 600, border: `1px solid ${STRICT_DARK_BORDER}` }}
                                                            />
                                                        ))}
                                                    </Box>
                                                </TableCell>

                                                <TableCell sx={{ borderBottom: `1px solid ${STRICT_DARK_BORDER}` }}>
                                                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                                                        {(item.data.rooms || []).map((r, i) => (
                                                            <Chip
                                                                key={i}
                                                                label={r.planname}
                                                                size="small"
                                                                sx={{ height: "22px", fontSize: "0.7rem", backgroundColor: "rgba(255, 213, 0, 0.05)", color: NEON_GREEN, fontWeight: 700, border: `1px solid rgba(209, 165, 5, 0.3)` }}
                                                            />
                                                        ))}
                                                    </Box>
                                                </TableCell>

                                                <TableCell align="center" sx={{ fontSize: "0.85rem", color: TEXT_PRIMARY, fontWeight: 600, borderBottom: `1px solid ${STRICT_DARK_BORDER}` }}>
                                                    {item.data.adults || 0}
                                                </TableCell>

                                                <TableCell align="right" sx={{ fontSize: "0.85rem", fontWeight: 700, color: TEXT_PRIMARY, borderBottom: `1px solid ${STRICT_DARK_BORDER}` }}>
                                                    ₹ {(item.data.totalamount || 0).toFixed(2)}
                                                </TableCell>

                                                <TableCell align="right" sx={{ fontSize: "0.85rem", fontWeight: 700, color: "#4ade80", borderBottom: `1px solid ${STRICT_DARK_BORDER}` }}>
                                                    ₹ {(item.data.advance || 0).toFixed(2)}
                                                </TableCell>

                                                <TableCell align="right" sx={{ fontSize: "0.85rem", fontWeight: 700, color: "#ef4444", borderBottom: `1px solid ${STRICT_DARK_BORDER}` }}>
                                                    ₹ {(item.data.remaining || 0).toFixed(2)}
                                                </TableCell>
                                            </TableRow>
                                        </Fade>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Interactive Pagination Navigation Bar */}
                        {totalPages > 1 && (
                            <Box display="flex" justifyContent="flex-end" mt={3}>
                                <Pagination
                                    count={totalPages}
                                    page={currentPage}
                                    onChange={(e, v) => setCurrentPage(v)}
                                    color="primary"
                                    size="medium"
                                    sx={{
                                        "& .MuiPaginationItem-root": { color: TEXT_PRIMARY, borderColor: STRICT_DARK_BORDER },
                                        "& .Mui-selected": { backgroundColor: `${NEON_GREEN} !important`, color: `${BG_DARK} !important`, fontWeight: 'bold' }
                                    }}
                                />
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
}

export default Viewreservations;